import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

export const formatUsPhone = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, "");
  // Support 10 digits, or 11 starting with 1 (US country code)
  let d = digits;
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length !== 10) return undefined;
  const area = d.slice(0, 3);
  const prefix = d.slice(3, 6);
  const line = d.slice(6);
  return `(${area}) ${prefix}-${line}`;
};

// ============================================================================
// Cloudflare Image Optimization
// ============================================================================

const CF_IMAGE_BASE = "https://travel.waypoint-bucket.dev";
const CF_CDN_PATH = "/cdn-cgi/image";
const CF_TRANSFORM_SEGMENT = `${CF_CDN_PATH}/`;
const LOCAL_PUBLIC_IMAGE_PREFIXES = ["/tenants/"] as const;

function isLocalPublicImage(path: string): boolean {
  return LOCAL_PUBLIC_IMAGE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export type CloudflareImageFormat = "auto" | "webp" | "avif" | "json";
export type CloudflareImageFit =
  | "scale-down"
  | "contain"
  | "cover"
  | "crop"
  | "pad";

export interface CloudflareImageOptions {
  /** Target width in pixels */
  width?: number;
  /** Target height in pixels */
  height?: number;
  /** Image quality (1-100), default 80 */
  quality?: number;
  /** Output format, default "auto" for browser-appropriate format */
  format?: CloudflareImageFormat;
  /** Resize behavior */
  fit?: CloudflareImageFit;
  /** Device pixel ratio for responsive images (1-3) */
  dpr?: number;
}

/**
 * Generate a Cloudflare-optimized image URL.
 * Uses Cloudflare Image Transformations for on-the-fly resizing, format conversion, and quality optimization.
 *
 * @example
 * // Basic usage - 800px wide, auto format
 * cfImage("/Hawaii.jpeg", { width: 800 })
 * // => "https://travel.waypoint-bucket.dev/cdn-cgi/image/width=800,quality=80,format=auto/Hawaii.jpeg"
 *
 * @example
 * // Full URL passthrough
 * cfImage("https://travel.waypoint-bucket.dev/Cancun.jpeg", { width: 600, quality: 75 })
 *
 * @example
 * // Responsive srcSet generation
 * cfImageSrcSet("/hero.jpg", [400, 800, 1200])
 */
export const cfImage = (
  path: string,
  options: CloudflareImageOptions = {}
): string => {
  const normalizedPath = path.trim();

  if (!normalizedPath) return path;

  if (
    normalizedPath.startsWith("data:") ||
    normalizedPath.startsWith("blob:") ||
    normalizedPath.startsWith("about:")
  ) {
    return path;
  }

  const lowerPath = normalizedPath.toLowerCase();
  if (
    lowerPath.endsWith(".svg") ||
    lowerPath.includes(".svg?") ||
    isLocalPublicImage(normalizedPath)
  ) {
    return path;
  }

  // If URL is not from our bucket, return as-is (external images)
  if (normalizedPath.startsWith("http") && !normalizedPath.includes("waypoint-bucket.dev")) {
    return path;
  }
  if (normalizedPath.includes(CF_TRANSFORM_SEGMENT)) {
    return path;
  }

  // Extract the relative path from full URL or use path directly
  const relativePath = normalizedPath.startsWith(CF_IMAGE_BASE)
    ? normalizedPath.slice(CF_IMAGE_BASE.length)
    : normalizedPath.startsWith("/")
    ? normalizedPath
    : `/${normalizedPath}`;

  // Build transformation parameters
  const params: string[] = [];

  if (options.width) params.push(`width=${options.width}`);
  if (options.height) params.push(`height=${options.height}`);
  params.push(`quality=${options.quality ?? 80}`);
  params.push(`format=${options.format ?? "auto"}`);
  if (options.fit) params.push(`fit=${options.fit}`);
  if (options.dpr && options.dpr > 1) params.push(`dpr=${options.dpr}`);

  return `${CF_IMAGE_BASE}${CF_CDN_PATH}/${params.join(",")}${relativePath}`;
};

/**
 * Generate a srcSet string for responsive images using Cloudflare transformations.
 *
 * @example
 * <img
 *   src={cfImage("/hero.jpg", { width: 800 })}
 *   srcSet={cfImageSrcSet("/hero.jpg", [400, 800, 1200, 1600])}
 *   sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 * />
 */
export const cfImageSrcSet = (
  path: string,
  widths: number[],
  options: Omit<CloudflareImageOptions, "width"> = {}
): string =>
  widths
    .map((w) => `${cfImage(path, { ...options, width: w })} ${w}w`)
    .join(", ");

/**
 * Preset configurations for common image use cases.
 */
export const cfImagePresets = {
  /** Thumbnail: 200px, quality 75 */
  thumbnail: (path: string) => cfImage(path, { width: 200, quality: 75 }),
  /** Card image: 400px */
  card: (path: string) => cfImage(path, { width: 400 }),
  /** Featured destination: 800px */
  featured: (path: string) => cfImage(path, { width: 800 }),
  /** Hero/banner: 1400px, quality 90 */
  hero: (path: string) => cfImage(path, { width: 1400, quality: 90 }),
  /** Full-width: 1920px */
  fullWidth: (path: string) => cfImage(path, { width: 1920 }),
  /** Logo: 400px, keep quality high */
  logo: (path: string) => cfImage(path, { width: 400, quality: 90 }),
  /** Small logo: 200px */
  logoSmall: (path: string) => cfImage(path, { width: 200, quality: 90 }),
} as const;

// Cloudflare sizing presets for location images (matches legacy R2 sizing)
export const CF_LOCATION_IMAGE_SIZES = {
  thumb: { width: 200, height: 250, quality: 80 },
  card: { width: 800, height: 1000, quality: 85 },
  hero: { width: 1400, height: 900, quality: 90 },
} as const;

export type CloudflareLocationImageSize = keyof typeof CF_LOCATION_IMAGE_SIZES;

export const cfLocationImage = (
  path: string,
  size: CloudflareLocationImageSize
): string => {
  const config = CF_LOCATION_IMAGE_SIZES[size];
  return cfImage(path, {
    width: config.width,
    height: config.height,
    quality: config.quality,
    fit: "cover",
    format: "webp",
  });
};
